import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { HelmetProvider } from 'react-helmet-async';
import theme from './theme';
import './styles/index.scss';
import Hotjar from './utils/Hotjar';
import IntercomMessenger from './utils/IntercomMessenger';
import Layout from './components/Layout';
import Projects from './pages/Projects';
import Dashboard from './pages/Dashboard';
import Pages from './pages/Pages';
import TestCases from './pages/TestCases';
import Discovery from './pages/Discovery';
import TestCase from './pages/TestCase';
import TestRun from './pages/TestRun/index';
import Profile from './pages/Profile';
import Tutorial from './pages/Tutorial';
import TaskManagement from './pages/TaskManagement';
import Login from './pages/Login';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailConfirmation from './pages/EmailConfirmation';
import EmailVerification from './pages/EmailVerification';
import SharedReport from './pages/SharedReport';
import ProjectSettings from './pages/ProjectSettings';
import { AuthProvider } from './context/AuthContext';
import { AppWithNotifications } from './context/NotificationContext';
import { AllProjectsProvider } from './context/AllProjectsContext';
import { ProjectProvider } from './context/ProjectContext';
import { SidebarProvider } from './context/SidebarContext';
import ProtectedRoute from './components/ProtectedRoute';
import addExternalScripts from './utils/addExternalScripts';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './react-query/queryClient';
import TestJourneysLayout from 'pages/TestJourneys/TestJourneysLayout';
import SingleJourneyLayout from 'pages/SingleJourney/SingleJourneyLayout';
import ChromeExtensionSetup from './pages/ChromeExtensionSetup/ChromeExtensionSetup';
import PagePrototypes from './pages/PagePrototypes';

addExternalScripts();

const router = createBrowserRouter([
  {
    path: '/page-prototypes',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [{ index: true, element: <PagePrototypes /> }],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <Projects /> },
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'pages', element: <Pages /> },
      { path: 'pages/:id', element: <Discovery /> },
      { path: 'test-cases', element: <TestCases /> },
      { path: 'test-case/:id', element: <TestCase /> },
      { path: 'test-run/:id', element: <TestRun /> },
      { path: 'project/:id', element: <ProjectSettings /> },
      { path: 'profile', element: <Profile /> },
      { path: 'tutorial', element: <Tutorial /> },
      { path: 'tasks', element: <TaskManagement /> },
      { path: 'report/:id', element: <SharedReport /> },
      { path: 'test-journeys', element: <TestJourneysLayout /> },
      { path: 'test-journey/:id', element: <SingleJourneyLayout /> },
      { path: 'chrome-extension-setup', element: <ChromeExtensionSetup /> },
    ],
  },
  { path: 'login', element: <Login /> },
  { path: 'forgot-password', element: <ForgotPassword /> },
  { path: 'reset-password/:token', element: <ResetPassword /> },
  { path: 'signup', element: <SignUp /> },
  { path: 'confirmation', element: <EmailConfirmation /> },
  { path: 'verify-email/:token', element: <EmailVerification /> },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppWithNotifications>
            <AllProjectsProvider>
              <ProjectProvider>
                <SidebarProvider>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <Hotjar />
                    <IntercomMessenger />
                    <RouterProvider router={router} />
                  </ThemeProvider>
                </SidebarProvider>
              </ProjectProvider>
            </AllProjectsProvider>
          </AppWithNotifications>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);
